import React, { useState, useEffect, useRef } from 'react';
import { BoardSelector } from './BoardSelector';
import { BoardToolbar } from './BoardToolbar';
import { KanbanBoard, Column, CardType } from './KanbanBoard';
import { auth } from '../../lib/firebase';
import {
  loadAllBoards, saveBoard, deleteBoard, subscribeToBoards, Board as FirestoreBoard, debouncedSaveBoard, clearBoardCache
} from '../../lib/huntersBoardService';
import TagSidebar from './TagSidebar';
import MethodologyPickerModal from './MethodologyPickerModal';
import { Methodology, GuidedMethodology } from '../../lib/firebase';
import WorkBoardCanvas from './WorkBoardCanvas';
import CardModal from './CardModal';

// Types
interface BoardSummary {
  id: string;
  name: string;
  createdAt: number;
  updatedAt: number;
}

interface Board {
  id: string;
  name: string;
  columns: Column[];
  cards: { [cardId: string]: CardType };
  createdAt: number;
  updatedAt: number;
  extra?: {
    cardPositions?: { [cardId: string]: { x: number; y: number } };
  };
}

// Helpers
const defaultColumns = [
  { id: 'scope', title: '🎯 Scope & Intel', cardIds: [] },
  { id: 'todo', title: '🧠 To-Do / Methodology', cardIds: [] },
  { id: 'progress', title: '⚙️ In Progress', cardIds: [] },
  { id: 'findings', title: '✅ Findings / Vulnerabilities', cardIds: [] },
  { id: 'resources', title: '📚 Resources & Loot', cardIds: [] },
  { id: 'reporting', title: '📝 Reporting', cardIds: [] },
];

const createNewBoard = (name: string): Board => {
  const now = Date.now();
  return {
    id: `board-${now}`,
    name,
    columns: defaultColumns.map(col => ({ ...col, cardIds: [] })),
    cards: {},
    createdAt: now,
    updatedAt: now,
  };
};

// Helper to normalize column titles (remove emojis/special chars)
function normalizeTitle(title: string) {
  return title.replace(/[^ -\w\s/-]/gi, '').toLowerCase();
}

const HuntersBoard: React.FC = () => {
  // Board state
  const [boards, setBoards] = useState<BoardSummary[]>([]);
  const [boardData, setBoardData] = useState<{ [id: string]: Board }>({});
  const [activeBoardId, setActiveBoardId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);

  // UI state
  const [search, setSearch] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [showTagSidebar, setShowTagSidebar] = useState(false);
  const [groupByTag, setGroupByTag] = useState(false);
  const [showMethodologyPicker, setShowMethodologyPicker] = useState(false);
  const [attachedMethodology, setAttachedMethodology] = useState<Methodology | GuidedMethodology | null>(null);
  const [checkedSteps, setCheckedSteps] = useState<string[]>([]);
  const [workBoardMode, setWorkBoardMode] = useState(false);
  const [stepState, setStepState] = useState<Record<string, { checked: boolean; note: string }>>({});

  // Real-time toggle for Firestore
  const [realtime, setRealtime] = useState(false);

  // Card modal state
  const [showCardModal, setShowCardModal] = useState(false);
  const [modalColId, setModalColId] = useState<string | null>(null);
  const [editCardId, setEditCardId] = useState<string | null>(null);

  // Card positions state for canvas mode
  const [canvasPositions, setCanvasPositions] = useState<{ [cardId: string]: { x: number; y: number } }>({});
  const syncTimer = useRef<NodeJS.Timeout | null>(null);

  // On mount, get user and load boards once
  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        setUserId(user.uid);
        setLoading(true);
        
        try {
          if (realtime) {
            // Real-time updates (higher quota usage) - only for collaborative features
            const unsubBoards = subscribeToBoards(user.uid, (fireBoards) => {
              setBoards(fireBoards.map(b => ({ id: b.id, name: b.name, createdAt: b.createdAt, updatedAt: b.updatedAt })));
              const data: { [id: string]: Board } = {};
              fireBoards.forEach(b => { data[b.id] = b; });
              setBoardData(data);
              if (!activeBoardId && fireBoards.length > 0) setActiveBoardId(fireBoards[0].id);
              setLoading(false);
            });
            return () => unsubBoards();
          } else {
            // Load all boards once and cache them (optimized for performance)
            const boards = await loadAllBoards(user.uid);
            setBoards(boards.map(b => ({ id: b.id, name: b.name, createdAt: b.createdAt, updatedAt: b.updatedAt })));
            const data: { [id: string]: Board } = {};
            boards.forEach(b => { data[b.id] = b; });
            setBoardData(data);
            if (!activeBoardId && boards.length > 0) setActiveBoardId(boards[0].id);
            setLoading(false);
          }
        } catch (error) {
          console.error('Error loading boards:', error);
          // Fallback to local state
          if (boards.length === 0) {
            const defaultBoard = createNewBoard('My First Board');
            setBoards([{ id: defaultBoard.id, name: defaultBoard.name, createdAt: defaultBoard.createdAt, updatedAt: defaultBoard.updatedAt }]);
            setBoardData({ [defaultBoard.id]: defaultBoard });
            setActiveBoardId(defaultBoard.id);
          }
          setLoading(false);
        }
      } else {
        setUserId(null);
        // Fallback to local state
        if (boards.length === 0) {
          const defaultBoard = createNewBoard('My First Board');
          setBoards([{ id: defaultBoard.id, name: defaultBoard.name, createdAt: defaultBoard.createdAt, updatedAt: defaultBoard.updatedAt }]);
          setBoardData({ [defaultBoard.id]: defaultBoard });
          setActiveBoardId(defaultBoard.id);
        }
        setLoading(false);
      }
    });
    return () => unsubscribe();
    // eslint-disable-next-line
  }, [realtime]);

  // Save board to Firestore on change (if authenticated)
  useEffect(() => {
    if (!userId || !activeBoardId) return;
    const board = boardData[activeBoardId];
    if (board) {
      debouncedSaveBoard(userId, board);
    }
  }, [userId, activeBoardId, boardData]);

  // Sync card positions to Firestore every 3 minutes
  useEffect(() => {
    if (!userId || !activeBoardId) return;
    if (!workBoardMode) return;
    const board = boardData[activeBoardId];
    if (!board) return;
    if (syncTimer.current) clearInterval(syncTimer.current);
    syncTimer.current = setInterval(() => {
      const board = boardData[activeBoardId];
      if (board) {
        const updated = { ...board, extra: { ...(board.extra || {}), cardPositions: canvasPositions } };
        debouncedSaveBoard(userId, updated);
      }
    }, 180000); // 3 minutes
    return () => { if (syncTimer.current) clearInterval(syncTimer.current); };
  }, [userId, activeBoardId, workBoardMode, canvasPositions, boardData]);

  // When loading board, load positions from board.extra.cardPositions if present
  useEffect(() => {
    const board = activeBoardId ? boardData[activeBoardId] : null;
    if (board && board.extra && board.extra.cardPositions) {
      setCanvasPositions(board.extra.cardPositions);
    }
  }, [activeBoardId, boardData]);

  // Derived
  const activeBoard = activeBoardId ? boardData[activeBoardId] : null;
  const allTags = activeBoard ? Array.from(
    new Set(
      Object.values(activeBoard.cards).flatMap(card => card.tags || [])
    )
  ) : [];
  const tagCounts = activeBoard ? allTags.reduce((acc, tag) => {
    acc[tag] = Object.values(activeBoard.cards).filter(card => card.tags?.includes(tag)).length;
    return acc;
  }, {} as Record<string, number>) : {};

  // Board actions - optimized for performance
  const handleSelectBoard = (id: string) => setActiveBoardId(id);
  
  const handleNewBoard = () => {
    const name = prompt('Board name?') || `Board ${boards.length + 1}`;
    const newBoard = createNewBoard(name);
    setBoards([...boards, { id: newBoard.id, name, createdAt: newBoard.createdAt, updatedAt: newBoard.updatedAt }]);
    setBoardData({ ...boardData, [newBoard.id]: newBoard });
    setActiveBoardId(newBoard.id);
    // Immediate save for new board creation (significant action)
    if (userId) saveBoard(userId, newBoard, true);
  };
  
  const handleRenameBoard = () => {
    if (!activeBoard) return;
    const name = prompt('New board name?', activeBoard.name);
    if (!name) return;
    setBoards(boards.map(b => b.id === activeBoardId ? { ...b, name } : b));
    setBoardData({ ...boardData, [activeBoardId!]: { ...activeBoard, name } });
    // Immediate save for board rename (significant action)
    if (userId) saveBoard(userId, { ...activeBoard, name }, true);
  };
  const handleDeleteBoard = () => {
    if (!activeBoardId || boards.length === 1) return alert('Cannot delete last board.');
    if (!window.confirm('Delete this board?')) return;
    setBoards(boards.filter(b => b.id !== activeBoardId));
    const newBoardData = { ...boardData };
    delete newBoardData[activeBoardId];
    setBoardData(newBoardData);
    const newActive = boards.find(b => b.id !== activeBoardId)?.id || null;
    setActiveBoardId(newActive);
    if (userId) deleteBoard(userId, activeBoardId);
  };

  // Kanban actions
  const handleAddColumn = () => {
    if (!activeBoard) return;
    const title = prompt('Column title?');
    if (!title) return;
    const id = `col-${Date.now()}`;
    const newCol = { id, title, cardIds: [] };
    setBoardData({
      ...boardData,
      [activeBoardId!]: {
        ...activeBoard,
        columns: [...activeBoard.columns, newCol],
      },
    });
  };
  const handleEditColumn = (colId: string, newTitle: string) => {
    if (!activeBoard) return;
    setBoardData({
      ...boardData,
      [activeBoardId!]: {
        ...activeBoard,
        columns: activeBoard.columns.map(col => col.id === colId ? { ...col, title: newTitle } : col),
      },
    });
  };
  const handleDeleteColumn = (colId: string) => {
    if (!activeBoard) return;
    if (!window.confirm('Delete this column?')) return;
    setBoardData({
      ...boardData,
      [activeBoardId!]: {
        ...activeBoard,
        columns: activeBoard.columns.filter(col => col.id !== colId),
      },
    });
  };
  const handleAddCard = (colId: string) => {
    setModalColId(colId);
    setEditCardId(null);
    setShowCardModal(true);
  };
  
  const handleEditCard = (colId: string, cardId: string) => {
    setModalColId(colId);
    setEditCardId(cardId);
    setShowCardModal(true);
  };
  const handleMoveCard = (result: any) => {
    if (!activeBoard) return;
    if (!result.destination) return;
    const { source, destination, draggableId } = result;
    if (source.droppableId === destination.droppableId && source.index === destination.index) return;
    
    // Remove from source
    let columns = activeBoard.columns.map(col => {
      if (col.id === source.droppableId) {
        const newCardIds = Array.from(col.cardIds);
        newCardIds.splice(source.index, 1);
        return { ...col, cardIds: newCardIds };
      }
      return col;
    });
    
    // Insert into destination
    columns = columns.map(col => {
      if (col.id === destination.droppableId) {
        const newCardIds = Array.from(col.cardIds);
        newCardIds.splice(destination.index, 0, draggableId);
        // Remove duplicates if any
        const seen = new Set<string>();
        const deduped = newCardIds.filter(id => (seen.has(id) ? false : seen.add(id)));
        return { ...col, cardIds: deduped };
      }
      return col;
    });
    
    const updatedBoard = {
      ...activeBoard,
      columns,
    };
    
    setBoardData({
      ...boardData,
      [activeBoardId!]: updatedBoard,
    });
    
    // Debounced save for card movement (minor change)
    if (userId) debouncedSaveBoard(userId, updatedBoard);
  };

  // Export
  const handleExport = () => {
    if (!activeBoard) return;
    let md = `# ${activeBoard.name}\n`;
    for (const col of activeBoard.columns) {
      md += `\n## ${col.title}\n`;
      for (const cardId of col.cardIds) {
        const card = activeBoard.cards[cardId];
        if (!card) continue;
        md += `\n### ${card.title}\n`;
        if (card.tags?.length) md += card.tags.map(t => `#${t}`).join(' ') + '\n';
        if (card.type === 'note' || card.type === 'resource') md += card.content + '\n';
        if (card.type === 'vuln') {
          md += `**Severity:** ${card.content.severity}\n`;
          md += `**Endpoint:** ${card.content.endpoint}\n`;
          md += `**Status:** ${card.content.status}\n`;
          md += `**Description:**\n${card.content.description}\n`;
          md += `**PoC:**\n${card.content.poc}\n`;
        }
        if (card.type === 'tool') md += `\n\   ${card.content.command}\   \n`;
        if (card.type === 'payload') md += `\n\   ${card.content.code}\   \n`;
      }
    }
    const blob = new Blob([md], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeBoard.name.replace(/\s+/g, '_')}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Tag logic
  const handleToggleTag = (tag: string) => {
    setSelectedTags(selectedTags.includes(tag)
      ? selectedTags.filter(t => t !== tag)
      : [...selectedTags, tag]);
  };
  const handleCreateTag = () => {
    const tag = prompt('New tag name?');
    // Tag creation logic can be expanded later
    if (tag && !allTags.includes(tag)) {
      // Optionally, add a dummy card with this tag to make it appear
      alert('Tag will appear when added to a card.');
    }
  };

  // Filtered cards by search/tag
  const filteredBoard = activeBoard ? {
    ...activeBoard,
    columns: activeBoard.columns.map(col => ({
      ...col,
      cardIds: col.cardIds.filter(cardId => {
        const card = activeBoard.cards[cardId];
        if (!card) return false;
        if (selectedTags.length && !selectedTags.every(tag => (card.tags || []).includes(tag))) return false;
        if (selectedTag && !(card.tags || []).includes(selectedTag)) return false;
        if (search && !card.title.toLowerCase().includes(search.toLowerCase()) && !JSON.stringify(card.content).toLowerCase().includes(search.toLowerCase())) return false;
        return true;
      }),
    })),
  } : null;

  // Handler for attaching methodology
  const handleAttachMethodology = (methodology: Methodology | GuidedMethodology) => {
    setAttachedMethodology(methodology);
    // Add steps as cards to the To-Do column
    if (!activeBoard) return;
    const todoCol = activeBoard.columns.find(col => col.title.includes('To-Do'));
    if (!todoCol) return;
    let steps: any[] = [];
    if ('steps' in methodology && Array.isArray(methodology.steps)) {
      steps = methodology.steps.map((step: any, idx: number) => ({
        id: `meth-step-${methodology.id}-${idx}`,
        type: 'note',
        title: step.title || step.name || `Step ${idx + 1}`,
        content: step.description || '',
        createdAt: Date.now(),
        updatedAt: Date.now(),
        tags: ['methodology'],
      }));
    } else if ('subMethodologies' in methodology && Array.isArray(methodology.subMethodologies)) {
      steps = methodology.subMethodologies.map((sub: any, idx: number) => ({
        id: `meth-step-${methodology.id}-${idx}`,
        type: 'note',
        title: sub.title || `Step ${idx + 1}`,
        content: sub.description || '',
        createdAt: Date.now(),
        updatedAt: Date.now(),
        tags: ['methodology'],
      }));
    }
    // Add steps as cards if not already present
    const newCards = { ...activeBoard.cards };
    const newCardIds = [...todoCol.cardIds];
    steps.forEach(step => {
      if (!newCards[step.id]) {
        newCards[step.id] = step;
        newCardIds.push(step.id);
      }
    });
    
    const updatedBoard = {
      ...activeBoard,
      cards: newCards,
      columns: activeBoard.columns.map(col =>
        col.id === todoCol.id ? { ...col, cardIds: newCardIds } : col
      ),
    };
    
    setBoardData({
      ...boardData,
      [activeBoardId!]: updatedBoard,
    });
    
    // Immediate save for methodology attachment (significant action)
    if (userId) saveBoard(userId, updatedBoard, true);
  };

  // Handler for step check
  const handleStepCheck = (cardId: string, checked: boolean) => {
    setStepState(prev => ({ ...prev, [cardId]: { ...prev[cardId], checked } }));
  };
  // Handler for step note
  const handleStepNoteChange = (cardId: string, note: string) => {
    setStepState(prev => ({ ...prev, [cardId]: { ...prev[cardId], note } }));
  };

  // Manual refresh for one-time fetch
  const handleManualRefresh = async () => {
    if (userId) {
      setLoading(true);
      try {
        // Clear cache and reload
        clearBoardCache(userId);
        const boards = await loadAllBoards(userId);
        setBoards(boards.map(b => ({ id: b.id, name: b.name, createdAt: b.createdAt, updatedAt: b.updatedAt })));
        const data: { [id: string]: Board } = {};
        boards.forEach(b => { data[b.id] = b; });
        setBoardData(data);
      } catch (error) {
        console.error('Error refreshing boards:', error);
      } finally {
        setLoading(false);
      }
    }
  };

  // Card modal handlers - optimized for performance
  const handleSaveCard = (colId: string, card: CardType) => {
    if (!activeBoard) return;
    const updatedBoard = {
      ...activeBoard,
      cards: { ...activeBoard.cards, [card.id]: card },
      columns: activeBoard.columns.map(col =>
        col.id === colId && !col.cardIds.includes(card.id)
          ? { ...col, cardIds: [...col.cardIds, card.id] }
          : col
      ),
    };
    
    setBoardData({
      ...boardData,
      [activeBoardId!]: updatedBoard,
    });
    setShowCardModal(false);
    
    // Immediate save for card creation/editing (significant action)
    if (userId) saveBoard(userId, updatedBoard, true);
  };

  const handleDeleteCard = (colId: string, cardId: string) => {
    if (!activeBoard) return;
    // Remove card from cards and from column
    const { [cardId]: _, ...restCards } = activeBoard.cards;
    const updatedBoard = {
      ...activeBoard,
      cards: restCards,
      columns: activeBoard.columns.map(col =>
        col.id === colId
          ? { ...col, cardIds: col.cardIds.filter(id => id !== cardId) }
          : col
      ),
    };
    
    setBoardData({
      ...boardData,
      [activeBoardId!]: updatedBoard,
    });
    
    // Immediate save for card deletion (significant action)
    if (userId) saveBoard(userId, updatedBoard, true);
  };

  if (loading) return <div className="p-8 text-center">Loading Hunter's Board...</div>;
  if (!activeBoard) {
    return (
      <div className="p-8 text-center">
        No board selected.<br />
        <button
          className="btn btn-primary mt-4"
          onClick={() => {
            const name = prompt('Board name?') || 'My First Board';
            const newBoard = createNewBoard(name);
            setBoards([{ id: newBoard.id, name: newBoard.name, createdAt: newBoard.createdAt, updatedAt: newBoard.updatedAt }]);
            setBoardData({ [newBoard.id]: newBoard });
            setActiveBoardId(newBoard.id);
            if (userId) saveBoard(userId, newBoard);
          }}
        >
          + Create Your First Board
        </button>
      </div>
    );
  }
  if (!filteredBoard || !filteredBoard.columns) {
    return (
      <div className="flex-1 p-4">
        <div className="text-center text-gray-500">No board or columns to display. Please create or select a board.</div>
      </div>
    );
  }

  // Force the Attach Methodology button to render in every column for debugging
  const kanbanColumns = filteredBoard.columns.map(col => ({
    ...col,
    extraHeader: (
      <button className="btn btn-xs mb-2" onClick={() => setShowMethodologyPicker(true)}>
        Attach Methodology
      </button>
    ),
  }));

  return (
    <div className="bg-white rounded-[20px] p-4 md:p-8 shadow-lg w-full max-w-full overflow-hidden">
      <div className="flex flex-col lg:flex-row h-full min-h-0">
        {showTagSidebar && (
          <div className="lg:w-64 flex-shrink-0 border-b lg:border-b-0 lg:border-r border-slate-200">
            <TagSidebar
              tags={allTags}
              tagCounts={tagCounts}
              selectedTags={selectedTags}
              onToggleTag={handleToggleTag}
              onCreateTag={handleCreateTag}
            />
          </div>
        )}
        <div className="flex-1 p-2 md:p-4 min-w-0 overflow-hidden">
          {/* Mobile-friendly controls */}
          <div className="space-y-3 mb-4">
            {/* Primary controls - always visible */}
            <div className="flex flex-wrap items-center gap-2">
              <button className="btn btn-xs" onClick={() => setShowTagSidebar(v => !v)}>
                {showTagSidebar ? 'Hide Tags' : 'Show Tags'}
              </button>
              {!realtime && (
                <button className="btn btn-xs" onClick={handleManualRefresh}>Refresh</button>
              )}
            </div>
            
            {/* Secondary controls - collapsible on mobile */}
            <div className="flex flex-wrap items-center gap-3 text-sm">
              <label className="flex items-center gap-1 cursor-pointer">
                <input type="checkbox" checked={groupByTag} onChange={e => setGroupByTag(e.target.checked)} />
                <span className="whitespace-nowrap">Group by Tag</span>
              </label>
              <label className="flex items-center gap-1 cursor-pointer">
                <input type="checkbox" checked={workBoardMode} onChange={e => setWorkBoardMode(e.target.checked)} />
                <span className="whitespace-nowrap">Canvas Mode</span>
              </label>
              <label className="flex items-center gap-1 cursor-pointer">
                <input type="checkbox" checked={realtime} onChange={e => setRealtime(e.target.checked)} />
                <span className="whitespace-nowrap">Real-time</span>
              </label>
            </div>
          </div>
          
          <BoardSelector
            boards={boards}
            activeBoardId={activeBoardId!}
            onSelect={handleSelectBoard}
            onNew={handleNewBoard}
            onRename={handleRenameBoard}
            onDelete={handleDeleteBoard}
          />
          <BoardToolbar
            search={search}
            setSearch={setSearch}
            tags={allTags}
            selectedTag={selectedTag}
            setSelectedTag={setSelectedTag}
            onExport={handleExport}
          />
          
          {/* Board container with proper overflow handling */}
          <div className="w-full h-full overflow-hidden">
            {workBoardMode ? (
              <div className="w-full h-full overflow-hidden">
                <WorkBoardCanvas
                  columns={filteredBoard.columns}
                  cards={activeBoard.cards}
                  boardId={activeBoard.id}
                  userId={userId || undefined}
                  onAddCard={handleAddCard}
                  onDeleteCard={handleDeleteCard}
                  onEditCard={handleEditCard}
                  onPositionsChange={setCanvasPositions}
                />
              </div>
            ) : (
              <div className="w-full h-full overflow-hidden">
                <KanbanBoard
                  columns={kanbanColumns}
                  cards={activeBoard.cards}
                  onAddCard={handleAddCard}
                  onMoveCard={handleMoveCard}
                  onEditColumn={handleEditColumn}
                  onDeleteColumn={handleDeleteColumn}
                  onAddColumn={handleAddColumn}
                  stepState={stepState}
                  onStepCheck={handleStepCheck}
                  onStepNoteChange={handleStepNoteChange}
                  onDeleteCard={handleDeleteCard}
                  onEditCard={handleEditCard}
                />
              </div>
            )}
          </div>
          
          <MethodologyPickerModal
            open={showMethodologyPicker}
            onClose={() => setShowMethodologyPicker(false)}
            onSelect={handleAttachMethodology}
          />
          <CardModal
            open={showCardModal}
            onClose={() => setShowCardModal(false)}
            onSave={handleSaveCard}
            colId={modalColId}
            card={editCardId ? activeBoard.cards[editCardId] : undefined}
            allCards={Object.values(activeBoard.cards)}
          />
        </div>
      </div>
    </div>
  );
};

export default HuntersBoard; 